import React from 'react';
import { useTranslation } from 'react-i18next';
import { addDays } from 'date-fns';
import stylesInvoice from './styles/LatestInvoice.module.scss';
import styles from './styles/MyBillsTableRow.module.scss';

import { OmaLummeApp } from '../../omaLummeContext';

import Loading from '../Common/Loading';

// Types.
import { myBillsTableRowTypes } from '../../types/billing';
// Utils.
import { formatDate, getVirtualBarCode, getCorrectBankAccount, setPriceFormat, roundNumber } from '../../utils/billing';
import { gaEventIfAllowed } from '../../utils/googleAnalytics';
import { useWindowSize } from '../Layout/Header';
import Accordion from '../Common/Accordion';

const MyBillsTableRow: React.FunctionComponent<myBillsTableRowTypes> = (props: myBillsTableRowTypes) => {
  const { t } = useTranslation();

  const { users } = OmaLummeApp.useState();

  const { invoice, desiredPdf, setDesiredPdf, mostRecentInvoiceDate, mostRecentInvoiceNumber } = props;
  const { postponed, setPostponeVisibility } = props.postpone;
  const [invoiceDate, setInvoiceDate] = React.useState<string>(invoice.dueDate);
  const [copyButtonText, setCopyButtonText] = React.useState<string>(`${t('billing:barcode.copyShort')}`);

  const customerId = users.activeUser.userId;
  const invoiceNumber = invoice.identifier;

  const windowWidth = useWindowSize()[0];
  const isMobile = windowWidth < 768;

  React.useEffect(() => {
    if (mostRecentInvoiceNumber === invoiceNumber && mostRecentInvoiceDate !== invoiceDate) {
      setInvoiceDate(mostRecentInvoiceDate);
    }
  }, [customerId, invoiceNumber, invoiceDate, mostRecentInvoiceDate, mostRecentInvoiceNumber]);

  const formattedPrice = `${setPriceFormat(roundNumber(invoice.totalWithVat))} €`;

  const invoicePdf = (
    <>
      <a
        href="#root"
        className={`${styles.link} underline`}
        onClick={(e): void => {
          e.preventDefault();
          setDesiredPdf(parseInt(invoice.identifier));
        }}
        aria-label={`${t('billing:billing.openPdfByDate')} ${formatDate(invoiceDate)}`}
      >
        {t('common:general.downloadButton')}
      </a>
      {desiredPdf && desiredPdf === parseInt(invoice.identifier) && <Loading size="small" />}
    </>
  );

  const bankAccount = getCorrectBankAccount(invoice.company);
  let code = '';
  let barcodeButton: React.JSX.Element = <></>;
  if (bankAccount) {
    code = getVirtualBarCode(bankAccount, invoice.totalWithVat, invoice.referenceNumber, invoiceDate);
  }

  if (code.length === 54) {
    const copyToClipboard = (str: string): void => {
      const el = document.createElement('textarea');
      el.value = str;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopyButtonText(`${t('billing:barcode.copied')}`);
      setTimeout((): void => {
        setCopyButtonText(`${t('billing:barcode.copyShort')}`);
      }, 3000);
    };
    barcodeButton = (
      <a
        href="#root"
        className={`${styles.link} underline`}
        onClick={(e): void => {
          e.preventDefault();
          gaEventIfAllowed({
            category: 'Kopioi viivakoodi',
            action: 'Kopioi virtuaaliviivakoodi - Laskuhistoria',
          });
          copyToClipboard(code);
        }}
      >
        {copyButtonText}
      </a>
    );
  }

  const idString = `${customerId}-${invoiceNumber}`;

  const postPonedString = invoice.dueDatePostponeable && postponed === idString;

  const invoiceState = invoice.status;

  if (isMobile) {
    return (
      <Accordion
        className="customerChild"
        title={
          <>
            <div className={styles.detail}>
              <span>{t('billing:billing.sortBillState')}</span>
              {postPonedString && <span className="postponeInvoice postponed">{t('consumption:consumption.postponed')}</span>}
              {!postPonedString && (
                <span className={invoice.status === 'Avoin' ? `${styles.alert} ${stylesInvoice.alert}` : ''}>{invoice.status}</span>
              )}
            </div>
            <div className={styles.detail}>
              <span>{t('billing:billing.sortBillSum')}</span>
              <span>{formattedPrice}</span>
            </div>
            <div className={styles.detail}>
              <span>{t('billing:billing.sortDueDate')}</span>
              <span>{postPonedString ? formatDate(addDays(new Date(invoiceDate), 14).toISOString()) : formatDate(invoiceDate)}</span>
            </div>
          </>
        }
      >
        {invoice.dueDatePostponeable && postponed !== idString ? (
          <div className={styles.detail}>
            <span></span>
            <span>
              <button className={stylesInvoice.postponeInvoice} onClick={(): void => setPostponeVisibility(idString)}>
                {t('consumption:consumption.postponeBill')}
              </button>
            </span>
          </div>
        ) : (
          <></>
        )}
        <div className={styles.detail}>
          <span>{t('billing:billing.sortInvoiceDate')}</span>
          <span>{formatDate(invoice.dateOfInvoice)}</span>
        </div>
        <div className={styles.detail}>
          <span>{t('billing:billing.sortBillNumber')}</span>
          <span>{invoice.identifier}</span>
        </div>
        <div className={styles.detail}>
          <span>{t('billing:billing.sortPdf')}</span>
          <span>{invoicePdf}</span>
        </div>
        <div className={styles.detail}>
          <span>{t('billing:billing.sortBarCode')}</span>
          <span>{code && barcodeButton}</span>
        </div>
      </Accordion>
    );
  } else {
    return (
      <div className={styles.item}>
        <div>
          {postPonedString && <span className="postponeInvoice postponed">{t('consumption:consumption.postponed')}</span>}
          {!postPonedString && <span className={invoiceState === 'Avoin' ? `${styles.alert} ${stylesInvoice.alert}` : ''}>{invoiceState}</span>}
        </div>
        <div>{formattedPrice}</div>
        <div>
          {postPonedString ? formatDate(addDays(new Date(invoiceDate), 14).toISOString()) : formatDate(invoiceDate)}
          {invoice.dueDatePostponeable && postponed !== idString && (
            <button className={stylesInvoice.postponeInvoice} onClick={(): void => setPostponeVisibility(idString)}>
              {t('consumption:consumption.postponeBillTable')}
            </button>
          )}
        </div>
        <div>{formatDate(invoice.dateOfInvoice)}</div>
        <div>{invoice.identifier}</div>
        <div>{invoicePdf}</div>
        <div>{code && barcodeButton}</div>
      </div>
    );
  }
};

export default MyBillsTableRow;
